import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, X, Upload, Download, Trash2, LayoutGrid, List, File, MessageSquare, Send, Activity } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SiteButton } from "@/components/brand/SiteButton";
import { cn } from "@/lib/utils";

const COLUMNS = [
  { key: "todo", label: "To do" },
  { key: "in-progress", label: "In progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];
const PRIORITY_BADGE = {
  low: "text-brand-mist", medium: "text-brand-azure", high: "text-yellow-300", urgent: "text-red-400",
};

const NewTaskModal = ({ team, onClose, onCreated, projectId }) => {
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", assignee_id: "", due_date: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, assignee_id: form.assignee_id || null, due_date: form.due_date || null };
      const r = await api.post(`/pms/projects/${projectId}/tasks`, payload);
      onCreated(r.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-black/70 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} data-testid="task-new-modal">
      <form onSubmit={submit} className="w-full max-w-lg space-y-4 rounded-t-2xl border border-brand-mist/15 bg-brand-charcoal p-6 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-brand-snow">New task</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1.5 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
        </div>
        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Task title" data-testid="task-title-input"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={2} data-testid="task-desc-input"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-priority-select"
            className="rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none">
            {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} data-testid="task-assignee-select"
            className="rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none">
            <option value="">Unassigned</option>
            {team.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
          </select>
        </div>
        <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <SiteButton type="button" variant="ghost" onClick={onClose}>Cancel</SiteButton>
          <SiteButton type="submit" disabled={saving} testid="task-create-submit">{saving ? "Adding…" : "Add task"}</SiteButton>
        </div>
      </form>
    </div>
  );
};

const TaskDetail = ({ task, onClose, onCommented }) => {
  const [comments, setComments] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const loadComments = () => {
    api.get(`/pms/tasks/${task.id}/comments`).then((r) => setComments(r.data)).catch(() => setComments([]));
  };
  useEffect(loadComments, [task.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/pms/tasks/${task.id}/comments`, { body: body.trim() });
      setBody("");
      loadComments();
      onCommented();
    } catch {}
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-black/70 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} data-testid="task-detail-modal">
      <div className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl border border-brand-mist/15 bg-brand-charcoal sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-brand-mist/15 p-5">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-brand-mist">{task.status} · {task.priority}</p>
            <h2 className="mt-1 font-display text-lg font-bold text-brand-snow">{task.title}</h2>
            {task.description && <p className="mt-1 text-sm text-brand-mist">{task.description}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="task-detail-close" className="rounded-md p-1.5 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-5" data-testid="task-comments">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-mist"><MessageSquare size={13} /> Comments</p>
          {comments === null && <p className="text-sm text-brand-mist">Loading…</p>}
          {comments !== null && comments.length === 0 && <p className="text-sm text-brand-mist/60">No comments yet. Start the conversation.</p>}
          {(comments || []).map((c) => (
            <div key={c.id} className="rounded-lg border border-brand-mist/12 bg-brand-black/40 p-3">
              <div className="flex items-center justify-between text-[11px] text-brand-mist">
                <span className="font-medium text-brand-emerald">{c.user_name || c.user_email}</span>
                <span>{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <p className="mt-1.5 text-sm text-brand-snow">{c.body}</p>
            </div>
          ))}
        </div>
        <form onSubmit={submit} className="flex items-center gap-2 border-t border-brand-mist/15 p-4">
          <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a comment…" data-testid="comment-input"
            className="flex-1 rounded-full border border-brand-mist/20 bg-brand-black/40 px-4 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
          <button type="submit" disabled={sending || !body.trim()} data-testid="comment-send"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-emerald text-brand-black disabled:opacity-40" aria-label="Send comment">
            <Send size={15} />
          </button>
        </form>
      </div>
    </div>
  );
};

const ProjectChat = ({ projectId }) => {
  const [messages, setMessages] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = () => {
    api.get(`/pms/projects/${projectId}/chat`).then((r) => setMessages(r.data)).catch(() => setMessages([]));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // poll for new messages
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/pms/projects/${projectId}/chat`, { body: body.trim() });
      setBody("");
      load();
    } catch {}
    setSending(false);
  };

  return (
    <div className="flex flex-col rounded-xl border border-brand-mist/12 bg-brand-charcoal" data-testid="project-chat">
      <h3 className="border-b border-brand-mist/15 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-brand-mist">Team chat</h3>
      <div ref={listRef} className="h-64 flex-1 space-y-2.5 overflow-y-auto p-4" data-testid="chat-messages">
        {messages === null && <p className="text-sm text-brand-mist">Loading…</p>}
        {messages !== null && messages.length === 0 && <p className="text-sm text-brand-mist/60">No messages yet. Say hello to the team.</p>}
        {(messages || []).map((m) => (
          <div key={m.id} className="rounded-lg border border-brand-mist/12 bg-brand-black/40 p-2.5">
            <div className="flex items-center justify-between text-[11px] text-brand-mist">
              <span className="font-medium text-brand-emerald">{m.user_name || m.user_email}</span>
              <span>{new Date(m.created_at).toLocaleTimeString()}</span>
            </div>
            <p className="mt-1 text-sm text-brand-snow">{m.body}</p>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-brand-mist/15 p-3">
        <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message the team…" data-testid="chat-input"
          className="flex-1 rounded-full border border-brand-mist/20 bg-brand-black/40 px-4 py-2 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        <button type="submit" disabled={sending || !body.trim()} data-testid="chat-send"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-emerald text-brand-black disabled:opacity-40" aria-label="Send">
          <Send size={14} />
        </button>
      </form>
    </div>
  );
};

const AdminProjectDetail = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [team, setTeam] = useState([]);
  const [view, setView] = useState("kanban");
  const [showNewTask, setShowNewTask] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [activity, setActivity] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);
  const [addMemberId, setAddMemberId] = useState("");
  const fileRef = useRef(null);

  const isAdmin = me?.role === "admin";
  const addMember = async () => {
    if (!addMemberId) return;
    setError("");
    try {
      await api.post(`/pms/projects/${projectId}/members/${addMemberId}`);
      setAddMemberId("");
      load();
      loadActivity();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };
  const removeMember = async (userId) => {
    setError("");
    try {
      await api.delete(`/pms/projects/${projectId}/members/${userId}`);
      load();
      loadActivity();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const load = () => {
    api.get(`/pms/projects/${projectId}`).then((r) => setProject(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Project not found"));
  };
  const loadActivity = () => {
    api.get(`/pms/projects/${projectId}/activity`).then((r) => setActivity(r.data)).catch(() => setActivity([]));
  };
  useEffect(() => {
    load();
    loadActivity();
    api.get("/pms/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const moveTask = async (taskId, status) => {
    await api.patch(`/pms/tasks/${taskId}`, { status });
    load();
    loadActivity();
  };
  const deleteTask = async (taskId) => {
    await api.delete(`/pms/tasks/${taskId}`);
    load();
  };
  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/pms/projects/${projectId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const deleteFile = async (fileId) => {
    await api.delete(`/pms/files/${fileId}`);
    load();
  };

  if (error && !project) return <p className="text-sm text-red-400" data-testid="project-error">{error}</p>;
  if (!project) return <p className="text-sm text-brand-mist">Loading…</p>;

  const tasks = project.tasks || [];
  const files = project.files || [];

  return (
    <div className="space-y-6" data-testid="admin-project-detail">
      <div>
        <Link to="/admin/projects" className="inline-flex items-center gap-1.5 text-xs text-brand-mist hover:text-brand-emerald" data-testid="project-back">
          <ArrowLeft size={14} /> Back to projects
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-brand-snow">{project.name}</h1>
            <p className="mt-1 text-sm text-brand-mist">{project.description}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-brand-mist">
              <span className="rounded-full border border-brand-mist/20 px-2.5 py-0.5">{project.service_type}</span>
              <span className="rounded-full border border-brand-mist/20 px-2.5 py-0.5">{project.status}</span>
              {project.deadline && <span className="rounded-full border border-brand-mist/20 px-2.5 py-0.5">Due {project.deadline}</span>}
            </div>
            {/* Team members + add/remove */}
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="team-members">
              <span className="text-xs font-semibold uppercase tracking-wider text-brand-mist">Team:</span>
              {(project.team_members || []).map((m) => m && (
                <span key={m.id} className="flex items-center gap-1.5 rounded-full border border-brand-emerald/30 bg-brand-emerald/10 px-2.5 py-0.5 text-[11px] text-brand-emerald">
                  {m.name || m.email}
                  {isAdmin && m.id !== me?.id && (
                    <button onClick={() => removeMember(m.id)} aria-label={`Remove ${m.name || m.email}`} data-testid={`remove-member-${m.id}`} className="text-brand-emerald/60 hover:text-red-400"><X size={11} /></button>
                  )}
                </span>
              ))}
              <select value={addMemberId} onChange={(e) => setAddMemberId(e.target.value)} data-testid="add-member-select"
                className="rounded-full border border-brand-mist/20 bg-brand-black/40 px-2.5 py-1 text-[11px] text-brand-snow focus:border-brand-emerald focus:outline-none">
                <option value="">+ Add member</option>
                {team.filter((t) => !(project.team || []).includes(t.id)).map((t) => (
                  <option key={t.id} value={t.id}>{t.name || t.email}</option>
                ))}
              </select>
              {addMemberId && (
                <button onClick={addMember} data-testid="add-member-btn" className="rounded-full bg-brand-emerald px-2.5 py-1 text-[11px] font-semibold text-brand-black">Add</button>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <SiteButton variant="ghost" onClick={() => setShowActivity((s) => !s)} testid="activity-toggle">
              <Activity size={15} className="mr-1.5 inline" /> Activity
            </SiteButton>
            <SiteButton onClick={() => setShowNewTask(true)} testid="task-new-button"><Plus size={16} className="mr-1.5 inline" /> Add task</SiteButton>
          </div>
        </div>
      </div>

      {/* Activity log */}
      {showActivity && (
        <div className="rounded-xl border border-brand-mist/12 bg-brand-charcoal p-5" data-testid="activity-log">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-mist">Project activity</h3>
          {activity === null && <p className="mt-2 text-sm text-brand-mist">Loading…</p>}
          {activity !== null && activity.length === 0 && <p className="mt-2 text-sm text-brand-mist/60">No activity yet.</p>}
          <ul className="mt-3 space-y-2">
            {(activity || []).map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-3 border-b border-brand-mist/10 pb-2 text-sm">
                <span className="text-brand-snow"><span className="font-medium text-brand-emerald">{a.user_name || a.user_email}</span> {a.action.replace(/_/g, " ")}{a.detail ? `: ${a.detail}` : ""}</span>
                <span className="shrink-0 text-[11px] text-brand-mist">{new Date(a.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* View toggle */}
      <div className="flex items-center gap-2" data-testid="view-toggle">
        <button onClick={() => setView("kanban")} data-testid="view-kanban" className={cn("flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs", view === "kanban" ? "border-brand-emerald bg-brand-emerald/15 text-brand-emerald" : "border-brand-mist/20 text-brand-mist")}>
          <LayoutGrid size={14} /> Kanban
        </button>
        <button onClick={() => setView("list")} data-testid="view-list" className={cn("flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs", view === "list" ? "border-brand-emerald bg-brand-emerald/15 text-brand-emerald" : "border-brand-mist/20 text-brand-mist")}>
          <List size={14} /> List
        </button>
      </div>

      {/* Kanban */}
      {view === "kanban" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="kanban-board">
          {COLUMNS.map((col) => {
            const colTasks = tasks.filter((t) => t.status === col.key);
            return (
              <div key={col.key} className="rounded-xl border border-brand-mist/12 bg-brand-black/40 p-3" data-testid={`kanban-col-${col.key}`}>
                <div className="mb-3 flex items-center justify-between px-1">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-mist">{col.label}</h3>
                  <span className="rounded-full bg-brand-mist/10 px-2 py-0.5 text-[10px] text-brand-mist">{colTasks.length}</span>
                </div>
                <div className="space-y-2.5">
                  {colTasks.map((t) => (
                    <div key={t.id} className="cursor-pointer rounded-lg border border-brand-mist/12 bg-brand-charcoal p-3 transition-colors hover:border-brand-emerald/40" data-testid={`task-card-${t.id}`} onClick={() => setOpenTask(t)}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-brand-snow">{t.title}</p>
                        <button onClick={(e) => { e.stopPropagation(); deleteTask(t.id); }} aria-label="Delete task" className="shrink-0 text-brand-mist/50 hover:text-red-400"><Trash2 size={13} /></button>
                      </div>
                      <p className={cn("mt-1 text-[11px] font-medium", PRIORITY_BADGE[t.priority])}>{t.priority}</p>
                      {t.assignee && <p className="mt-1 text-[11px] text-brand-mist">{t.assignee.name || t.assignee.email}</p>}
                      <div className="mt-2.5 flex gap-1">
                        {COLUMNS.filter((c) => c.key !== t.status).map((c) => (
                          <button key={c.key} onClick={() => moveTask(t.id, c.key)} data-testid={`task-move-${t.id}-${c.key}`}
                            className="rounded border border-brand-mist/15 px-1.5 py-0.5 text-[10px] text-brand-mist hover:border-brand-emerald/40 hover:text-brand-emerald">
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {colTasks.length === 0 && <p className="px-1 py-2 text-center text-[11px] text-brand-mist/50">No tasks</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List */}
      {view === "list" && (
        <div className="overflow-x-auto rounded-xl border border-brand-mist/12" data-testid="task-list">
          <table className="w-full min-w-[560px] text-left">
            <thead>
              <tr className="border-b border-brand-mist/15 bg-brand-black/40 text-[11px] uppercase tracking-wider text-brand-mist">
                <th className="px-4 py-3">Task</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Priority</th><th className="px-4 py-3">Assignee</th><th className="px-4 py-3">Due</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id} className="border-b border-brand-mist/10" data-testid={`task-row-${t.id}`}>
                  <td className="px-4 py-3 text-sm text-brand-snow">{t.title}</td>
                  <td className="px-4 py-3">
                    <select value={t.status} onChange={(e) => moveTask(t.id, e.target.value)} data-testid={`task-status-${t.id}`}
                      className="rounded border border-brand-mist/20 bg-brand-black/40 px-2 py-1 text-xs text-brand-snow">
                      {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </td>
                  <td className={cn("px-4 py-3 text-xs font-medium", PRIORITY_BADGE[t.priority])}>{t.priority}</td>
                  <td className="px-4 py-3 text-xs text-brand-mist">{t.assignee ? (t.assignee.name || t.assignee.email) : "—"}</td>
                  <td className="px-4 py-3 text-xs text-brand-mist">{t.due_date || "—"}</td>
                  <td className="px-4 py-3"><button onClick={() => deleteTask(t.id)} aria-label="Delete task" className="text-brand-mist/50 hover:text-red-400"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {tasks.length === 0 && <p className="p-6 text-center text-sm text-brand-mist">No tasks yet.</p>}
        </div>
      )}

      {/* Files + Chat */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ProjectChat projectId={projectId} />
        <div className="rounded-xl border border-brand-mist/12 bg-brand-charcoal p-5" data-testid="project-files">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-base font-semibold text-brand-snow">Project files</h2>
          <div>
            <input ref={fileRef} type="file" onChange={uploadFile} className="hidden" id="file-upload" data-testid="file-upload-input" />
            <SiteButton onClick={() => fileRef.current?.click()} disabled={uploading} testid="file-upload-button" size="sm">
              <Upload size={14} className="mr-1.5 inline" /> {uploading ? "Uploading…" : "Upload file"}
            </SiteButton>
          </div>
        </div>
        {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
        <div className="mt-4 space-y-2">
          {files.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-lg border border-brand-mist/12 bg-brand-black/40 px-4 py-2.5" data-testid={`file-row-${f.id}`}>
              <div className="flex items-center gap-3">
                <File size={16} className="text-brand-mist" />
                <div>
                  <p className="text-sm text-brand-snow">{f.original_filename}</p>
                  <p className="text-[11px] text-brand-mist">{(f.size / 1024).toFixed(1)} KB · {f.uploaded_by_user ? (f.uploaded_by_user.name || f.uploaded_by_user.email) : "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pms/files/${f.id}/download`} download data-testid={`file-download-${f.id}`}
                  className="rounded-md p-1.5 text-brand-mist hover:text-brand-emerald"><Download size={15} /></a>
                <button onClick={() => deleteFile(f.id)} aria-label="Delete file" className="rounded-md p-1.5 text-brand-mist/50 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
          {files.length === 0 && <p className="py-3 text-center text-sm text-brand-mist">No files uploaded yet.</p>}
        </div>
      </div>
      </div>

      {showNewTask && <NewTaskModal team={team} projectId={projectId} onClose={() => setShowNewTask(false)} onCreated={() => { setShowNewTask(false); load(); loadActivity(); }} />}
      {openTask && <TaskDetail task={openTask} onClose={() => setOpenTask(null)} onCommented={loadActivity} />}
    </div>
  );
};

export default AdminProjectDetail;
